import { useState, useRef, useCallback } from 'react';

export interface UseVoicePreviewReturn {
  currentlyPlaying: string | null;
  isLoading: string | null;
  play: (voiceId: string, previewUrl: string) => Promise<void>;
  pause: () => void;
  isPlaying: (voiceId: string) => boolean;
}

export function useVoicePreview(): UseVoicePreviewReturn {
  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const pause = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setCurrentlyPlaying(null);
    setIsLoading(null);
  }, []);

  const play = useCallback(async (voiceId: string, previewUrl: string) => {
    // If this voice is already playing, pause it
    if (currentlyPlaying === voiceId) {
      pause();
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }

    setIsLoading(voiceId);
    
    try {
      const audio = new Audio(previewUrl);
      audioRef.current = audio;

      // Setup event listeners
      audio.addEventListener('loadeddata', () => {
        setIsLoading(null);
        setCurrentlyPlaying(voiceId);
      });

      audio.addEventListener('ended', () => {
        setCurrentlyPlaying(null);
        audioRef.current = null;
      });

      audio.addEventListener('error', () => {
        setIsLoading(null);
        setCurrentlyPlaying(null);
        audioRef.current = null;
        console.error('Failed to load audio preview for voice:', voiceId);
      });

      // Start playing
      await audio.play();
    } catch (error) {
      setIsLoading(null);
      setCurrentlyPlaying(null);
      audioRef.current = null;
      console.error('Failed to play audio preview:', error);
    }
  }, [currentlyPlaying, pause]);

  const isPlaying = useCallback((voiceId: string) => {
    return currentlyPlaying === voiceId;
  }, [currentlyPlaying]);

  return {
    currentlyPlaying,
    isLoading,
    play,
    pause,
    isPlaying
  };
} 